import React, { useEffect, useState } from "react";
import {
  Badge,
  Box,
  Flex,
  Heading,
  HStack,
  IconButton,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  VStack,
} from "@chakra-ui/react";
import { CherryPayApi } from "../../api/models";
import { Text } from "@chakra-ui/layout";
import NotificationUtil from "../../util/NotificationUtil";
import { useBusinessContext } from "../../context/ModelContext";
import { ImagePreview } from "../ImagePreview/ImagePreview";
import { isEmptyStr } from "../../util/StringUtil";
import { NotificationScheduleDetails } from "../NotificationScheduleDetails/NotificationScheduleDetails";
import {
  CloneIcon,
  ContextMenuIcon,
  EditIcon,
  RevertIcon,
  TrashIcon,
} from "../../styles/icons";
import { useApiClient } from "../../hooks/useApiClient";

interface NotificationDetailCardProps {
  notification: CherryPayApi.CherryPassNotificationSchedule;
  onEdit: (notification: CherryPayApi.CherryPassNotificationSchedule) => void;
  onClone: (notification: CherryPayApi.CherryPassNotificationSchedule) => void;
  onDelete: (notification: CherryPayApi.CherryPassNotificationSchedule) => void;
  onRevert: (notification: CherryPayApi.CherryPassNotificationSchedule) => void;
}

export const NotificationDetailCard = ({
  notification,
  onEdit,
  onClone,
  onDelete,
  onRevert,
}: NotificationDetailCardProps) => {
  const businessSummary = useBusinessContext();
  const [memberCount, setMemberCount] = useState<number | null>(null);
  const apiClient = useApiClient();

  // Fetch member count for saved notification
  useEffect(() => {
    if (
      businessSummary.BusinessId &&
      notification.MemberFilterQuery !== undefined &&
      (notification.NotificationScheduleStatus === "Draft" ||
        notification.NotificationScheduleStatus === "Scheduled")
    ) {
      setMemberCount(null);
      apiClient
        .getNotificationScheduleEligibleMemberCount(
          businessSummary.BusinessId,
          notification.MemberFilterQuery
        )
        .then((result) => {
          if (result.ok) {
            setMemberCount(result.data.Count ?? 0);
          } else {
            setMemberCount(null);
            console.error(
              "Failed to fetch notification member count:",
              result.message
            );
          }
        });
    }
  }, [apiClient, businessSummary.BusinessId, notification.MemberFilterQuery]);

  return (
    <Box position={"relative"} w="100%" p={0}>
      <Flex position="absolute" top="0" right="0" zIndex="1">
        <Menu>
          <MenuButton
            as={IconButton}
            aria-label="Options"
            icon={<ContextMenuIcon />}
            variant="ghost"
            size="sm"
          />
          <MenuList>
            {notification.NotificationScheduleStatus === "Draft" && (
              <MenuItem
                onClick={() => onEdit(notification)}
                icon={<EditIcon />}
              >
                Edit Push Notification
              </MenuItem>
            )}
            {notification.NotificationScheduleStatus === "Scheduled" && (
              <MenuItem
                onClick={() => onRevert(notification)}
                icon={<RevertIcon />}
              >
                Revert to Draft
              </MenuItem>
            )}
            <MenuItem
              onClick={() => onClone(notification)}
              icon={<CloneIcon />}
            >
              Clone Push Notification
            </MenuItem>
            {notification.NotificationScheduleStatus === "Draft" && (
              <MenuItem
                onClick={() => onDelete(notification)}
                color="red.500"
                icon={<TrashIcon />}
              >
                Delete Push Notification
              </MenuItem>
            )}
          </MenuList>
        </Menu>
      </Flex>
      <VStack align="start" spacing={4} w="100%">
        <HStack justifyContent="flex-start" w="90%">
          <Heading as="h2" size="lg">
            {notification.DisplayName}
          </Heading>
          <Badge
            px={3}
            py={1}
            borderRadius="full"
            background={NotificationUtil.getStatusColorScheme(
              notification.NotificationScheduleStatus
            )}
            fontSize="xs"
            fontWeight="bold"
            color={"white"}
            textTransform="uppercase"
          >
            {notification.NotificationScheduleStatus}
          </Badge>
        </HStack>
        <Text>{notification.PassUpdateMessage}</Text>
        <ImagePreview
          businessDisplayName={businessSummary.DisplayName}
          lockScreenMessage={
            isEmptyStr(notification.PassUpdateMessage)
              ? "No push notification configured."
              : notification.PassUpdateMessage!
          }
          backOfPassMessage={
            isEmptyStr(notification.PassUpdateMessage)
              ? "This campaign only uses geo-location messages. Notifications are triggered when members enter a configured location."
              : notification.PassUpdateMessage!
          }
        />
        <NotificationScheduleDetails
          notification={notification}
          memberCount={memberCount}
          businessId={businessSummary.BusinessId}
        />
      </VStack>
    </Box>
  );
};
